import React from 'react';
import { theme, Typography } from 'antd';
import type { CollapseProps } from 'antd';

import { SetupChecklistStateCompany, SETUP_CHECKLIST_KEYS, type SetupChecklistKey } from './SetupChecklist';
import { Title, Subtitle } from './ChecklistItemHeader';
import { ChecklistItemActions } from './ChecklistItemActions';
import { type SetupChecklistUpdateCompany } from './useSetupChecklistState';
import { themeConfig } from '../../theme';

const { getDesignToken } = theme;
const token = getDesignToken(themeConfig);

const ITEM_STYLE = { header: { alignItems: 'center' } };

const getPanelStyle = (active: boolean): React.CSSProperties => {
  return {
    marginBottom: token.marginMD,
    background: token.colorWhite,
    borderRadius: token.borderRadiusLG,
    border: `1px solid ${active ? token.colorPrimary : '#f0f0f0'}`,
    transition: 'border 0.3s ease-in-out',
  };
};

function ChecklistItemContent({ children }: React.PropsWithChildren) {
  return <div style={{ paddingLeft: 32 }}>{children}</div>;
}

function SetupChecklistCollapsibleItem(
  setupChecklistState: SetupChecklistStateCompany,
  activeKey: SetupChecklistKey[],
  markAsDone: (patch: SetupChecklistUpdateCompany['state']) => void
): CollapseProps['items'] {
  return [
    {
      styles: ITEM_STYLE,
      key: SETUP_CHECKLIST_KEYS.sitemap,
      label: (
        <Title
          title="Submit your sitemap"
          isCompleted={setupChecklistState.sitemap}
          subtitle={
            <Subtitle
              itemKey={SETUP_CHECKLIST_KEYS.sitemap}
              isCompleted={setupChecklistState.sitemap}
              reviewLink="/sitemaps"
              reviewText="Review or add more sitemaps"
              description="Pre-cache pages for faster bot responses."
            />
          }
        />
      ),
      children: (
        <ChecklistItemContent>
          <Typography.Paragraph type="secondary">
            Don&apos;t make bots wait. Submit your sitemap now, we&apos;ll pre-cache pages in advance, and bots will get instant
            responses when they visit your site.
          </Typography.Paragraph>
          <Typography.Text type="secondary">
            <Typography.Text strong>Tip:</Typography.Text> Choose your sitemap revisit interval to control how often we
            check for new pages. Faster intervals keep your cache fresh for frequently updated sites.
          </Typography.Text>
          <ChecklistItemActions
            primaryButtonLabel="Submit sitemap"
            primaryButtonLink="/sitemaps"
            learnMoreHref="https://docs.prerender.io/docs/sitemap"
            onMarkAsDone={setupChecklistState.sitemap ? undefined : () => markAsDone({ sitemap: true })}
            itemKey={SETUP_CHECKLIST_KEYS.sitemap}
          />
        </ChecklistItemContent>
      ),
      style: getPanelStyle(activeKey.includes(SETUP_CHECKLIST_KEYS.sitemap)),
    },
    {
      styles: ITEM_STYLE,
      key: SETUP_CHECKLIST_KEYS.urlParameters,
      label: (
        <Title
          title="Set up Caching Rules"
          isCompleted={setupChecklistState.urlParameters}
          subtitle={
            <Subtitle
              itemKey={SETUP_CHECKLIST_KEYS.urlParameters}
              isCompleted={setupChecklistState.urlParameters}
              reviewLink="/cache/caching-rules"
              reviewText="Review or add more parameters"
              description="Prevent duplicate caching and save your renders."
            />
          }
        />
      ),
      children: (
        <ChecklistItemContent>
          <Typography.Paragraph type="secondary">
            Parameters like ?utm_source or ?session_id create separate cache entries for the same page, wasting your
            render quota. Tell us which parameters don&apos;t affect your content and we&apos;ll ignore them when caching.
          </Typography.Paragraph>
          <ChecklistItemActions
            primaryButtonLabel="Configure parameters"
            primaryButtonLink="/cache/caching-rules"
            learnMoreHref="https://docs.prerender.io/docs/ignore-config#ignore-urls-based-on-rules:~:text=the%20prerendering%20service.-,Ignore%20Specific%20URL%20Parameters,-URL%20parameters%20can"
            onMarkAsDone={setupChecklistState.urlParameters ? undefined : () => markAsDone({ urlParameters: true })}
            itemKey={SETUP_CHECKLIST_KEYS.urlParameters}
          />
        </ChecklistItemContent>
      ),
      style: getPanelStyle(activeKey.includes(SETUP_CHECKLIST_KEYS.urlParameters)),
    },
    {
      styles: ITEM_STYLE,
      key: SETUP_CHECKLIST_KEYS.ignoredUrls,
      label: (
        <Title
          title="Set up caching rules: Ignore URLs"
          isCompleted={setupChecklistState.ignoredUrls}
          subtitle={
            <Subtitle
              itemKey={SETUP_CHECKLIST_KEYS.ignoredUrls}
              isCompleted={setupChecklistState.ignoredUrls}
              reviewLink="/cache/ignored-urls"
              reviewText="Review or add more rules"
              description="Prevent duplicate caching and save your renders."
            />
          }
        />
      ),
      children: (
        <ChecklistItemContent>
          <Typography.Paragraph type="secondary">
            Create rules to block URL patterns from caching entirely. Useful for preview pages, internal routes, or any
            URL that bots don&apos;t need to see. Save your monthly renders for pages that matter.
          </Typography.Paragraph>
          <ChecklistItemActions
            primaryButtonLabel="Setup rules"
            primaryButtonLink="/cache/ignored-urls"
            learnMoreHref="https://docs.prerender.io/docs/ignore-config#ignore-urls-based-on-rules"
            itemKey={SETUP_CHECKLIST_KEYS.ignoredUrls}
            onMarkAsDone={setupChecklistState.ignoredUrls ? undefined : () => markAsDone({ ignoredUrls: true })}
          />
        </ChecklistItemContent>
      ),
      style: getPanelStyle(activeKey.includes(SETUP_CHECKLIST_KEYS.ignoredUrls)),
    },
    {
      styles: ITEM_STYLE,
      key: SETUP_CHECKLIST_KEYS.cacheExpiration,
      label: (
        <Title
          title="Review cache expiration settings"
          isCompleted={setupChecklistState.cacheExpiration}
          subtitle={
            <Subtitle
              itemKey={SETUP_CHECKLIST_KEYS.cacheExpiration}
              isCompleted={setupChecklistState.cacheExpiration}
              reviewLink="/cache/cache-manager"
              reviewText="Review or adjust settings"
              description="Control how fresh your cached pages are."
            />
          }
        />
      ),
      children: (
        <ChecklistItemContent>
          <Typography.Paragraph type="secondary">
            If your content changes often, shorter cache intervals ensure bots see your latest updates. Stale cached
            pages mean search results could show outdated info to users. Match your cache expiration interval to how
            frequently your site actually changes.
          </Typography.Paragraph>
          <ChecklistItemActions
            primaryButtonLabel="Review settings"
            primaryButtonLink="/cache/cache-manager"
            learnMoreHref="https://docs.prerender.io/docs/modify-the-cache-expiration"
            onMarkAsDone={setupChecklistState.cacheExpiration ? undefined : () => markAsDone({ cacheExpiration: true })}
            itemKey={SETUP_CHECKLIST_KEYS.cacheExpiration}
          />
        </ChecklistItemContent>
      ),
      style: getPanelStyle(activeKey.includes(SETUP_CHECKLIST_KEYS.cacheExpiration)),
    },
    {
      styles: ITEM_STYLE,
      key: SETUP_CHECKLIST_KEYS.notFoundChecker,
      label: (
        <Title
          title="Set up 404 Checker"
          isCompleted={setupChecklistState.notFoundChecker}
          subtitle={
            <Subtitle
              itemKey={SETUP_CHECKLIST_KEYS.notFoundChecker}
              isCompleted={setupChecklistState.notFoundChecker}
              reviewLink="/domain-404-checker"
              reviewText="Review configuration"
              description="Don&apos;t waste renders on soft 404s."
            />
          }
        />
      ),
      children: (
        <ChecklistItemContent>
          <Typography.Paragraph type="secondary">
            If your site returns a 200 OK-status code for pages that don&apos;t exist (soft 404s), Google could index them
            and we may cache them. Set up 404 Checker so we avoid this entirely.
          </Typography.Paragraph>
          <ChecklistItemActions
            primaryButtonLabel="Enable 404 Checker"
            primaryButtonLink="/domain-404-checker"
            learnMoreHref="https://docs.prerender.io/docs/404-checker"
            onMarkAsDone={setupChecklistState.notFoundChecker ? undefined : () => markAsDone({ notFoundChecker: true })}
            itemKey={SETUP_CHECKLIST_KEYS.notFoundChecker}
          />
        </ChecklistItemContent>
      ),
      style: getPanelStyle(activeKey.includes(SETUP_CHECKLIST_KEYS.notFoundChecker)),
    },
  ];
}

export default SetupChecklistCollapsibleItem;
